/**
 * Types voor de rijke document-template-configuratie (docgen-uitbreiding).
 *
 * `placeholderConfig` verving de eerdere `placeholders: string[]` op
 * `ClientDocumentTemplate`: per merge-veld een label/type/group/required +
 * optionele declaratieve `autoFillFrom`. Gedeeld tussen API, backoffice en seed.
 *
 * Bewust géén Prisma-import: het category-type is een string-union die de
 * Prisma-enum `DocumentTemplateCategory` spiegelt (de types-package mag niet
 * runtime-afhankelijk zijn van @streep/db).
 */
import { canonicalPlaceholderKey } from './placeholder-canonicalisatie'

/** Veldtype voor het dynamische invulformulier per placeholder. */
export type PlaceholderFieldType =
  | 'text'
  | 'textarea'
  | 'date'
  | 'number'
  | 'currency'
  | 'select'

export const PLACEHOLDER_FIELD_TYPES: readonly PlaceholderFieldType[] = [
  'text',
  'textarea',
  'date',
  'number',
  'currency',
  'select',
] as const

export const PLACEHOLDER_FIELD_TYPE_LABELS: Record<PlaceholderFieldType, string> = {
  text: 'Tekst',
  textarea: 'Tekstblok',
  date: 'Datum',
  number: 'Getal',
  currency: 'Bedrag',
  select: 'Keuze',
}

/**
 * Configuratie voor één placeholder in een DOCX-template. Opgeslagen als
 * JSON-array in `ClientDocumentTemplate.placeholderConfig`.
 */
export interface PlaceholderConfig {
  /** Exacte placeholder-key zoals in de DOCX, bijv. "bvNaam" (zonder {{ }}). */
  key: string
  /** Label in het invulformulier, bijv. "Bedrijfsnaam klant". */
  label: string
  /** Veldtype voor het formulier. */
  type: PlaceholderFieldType
  /** Verplicht veld? */
  required: boolean
  /** Groep voor visuele indeling, bijv. "Klantgegevens", "STREEP". */
  group: string
  /** Default-waarde (vooraf ingevuld in het formulier). */
  defaultValue?: string
  /** Opties voor `type: 'select'`. */
  options?: Array<{ value: string; label: string }>
  /**
   * Declaratieve auto-fill bron. Formaat: "client.<veld>", "entity.<veld>",
   * "employee.<veld>", "voertuig.<veld>", "item.<veld>" (FO-421: het in de
   * bruikleen-wizard gekozen gebruiksitem) of "today". Bij selectie van een
   * relatie/entiteit/medewerker/item wordt dit veld automatisch ingevuld.
   * Voorbeelden: "client.name", "client.fullAddress", "entity.kvkNumber",
   * "voertuig.kenteken", "item.serienummer", "today".
   */
  autoFillFrom?: string
  /**
   * FO-453 (batch-61) - scan-synchronisatie. `false` = de laatste scan van de
   * DOCX vond deze key (canoniek) NIET meer: het veld is "verdwenen uit het
   * sjabloon" en wordt in de editor gemarkeerd, nooit stil verwijderd (beheer
   * beslist). `true`/ontbrekend = aanwezig of nog nooit gesynchroniseerd.
   */
  inSjabloon?: boolean
  /**
   * FO-453 - deze rij is door de auto-hersync uit de scan toegevoegd (optioneel
   * veld, niet-verplicht). Alleen informatief voor de editor ("nieuw uit scan").
   */
  uitScan?: boolean
}

/** Eén canoniek gegeven uit de scan, met al zijn schrijfwijzen (FO-410). */
export interface PlaceholderScanGroep {
  /** De canonieke key (`opdrachtgevernaam`). */
  canoniekeKey: string
  /** De netste schrijfwijze - dit wordt de veld-key in wizard en config. */
  primaireKey: string
  /** Alle schrijfwijzen in het sjabloon, in scan-volgorde. */
  varianten: string[]
  /** Aantal verschillende schrijfwijzen ("komt {n}x voor in het sjabloon"). */
  aantal: number
}

/** Resultaat van het scannen van een DOCX op {{...}}-placeholders. */
export interface PlaceholderScanResult {
  /** Gesorteerde, unieke placeholder-keys gevonden in het template. */
  keys: string[]
  /** Totaal aantal placeholder-voorkomens (incl. duplicaten). */
  totalOccurrences: number
  /**
   * FO-410 (batch-54) - dezelfde keys, gegroepeerd op canonieke key: één entry
   * per gegeven, ook als het sjabloon twee schrijfwijzen draagt. De wizard en de
   * config-editor bouwen hierop één veld per gegeven.
   */
  canoniekeGroepen: PlaceholderScanGroep[]
}

// ─── FO-401: sjabloon-lint (kwaliteitswaarschuwingen bij scan/upload) ────────

/**
 * FO-401 §B - lint-code van een sjabloon-kwaliteitswaarschuwing. Blokkeert
 * nooit een upload; maakt zichtbaar wat in het eindproduct misgaat.
 */
export type TemplateLintCode =
  /** Gemarkeerde (gele) tekst buiten een `{{...}}`-tag: vergeten invulveld. */
  | 'HIGHLIGHT_BUITEN_TAG'
  /** `{{...naam}}` direct gevolgd door een rechtsvorm: dubbele "B.V.". */
  | 'DUBBELE_RECHTSVORM'
  /** Twee-koloms handtekenblok op tabs/spaties: breekt in de PDF-conversie. */
  | 'HANDTEKENBLOK_TABS'
  /** Pagina-einde als eerste element: levert een lege eerste pagina op. */
  | 'LEEG_EERSTE_PAGINA_RISICO'

/** Eén sjabloon-lint-bevinding (FO-401 §B). */
export interface TemplateLintWaarschuwing {
  code: TemplateLintCode
  /** Leesbare melding voor het templatebeheer-scherm. */
  melding: string
  /** Concreet advies: wat te wijzigen in het sjabloon. */
  advies: string
  /** Voorbeeld-fragment uit het sjabloon (max ~80 tekens), indien zinvol. */
  fragment?: string
}

// ─── FO-401: waarschuwingen bij het genereren van een document ───────────────

/** FO-401 - waarschuwingscode bij documentgeneratie (nooit blokkerend). */
export type DocumentGeneratieWaarschuwingCode =
  /** De briefpapier-overlay faalde; het document is kaal geconverteerd. */
  | 'HUISSTIJL_TERUGVAL'
  /** Er is geen briefpapier gevonden voor deze BV/dit sjabloon. */
  | 'GEEN_BRIEFPAPIER'
  /** Pagina 1 is (vrijwel) leeg terwijl de inhoud pas op pagina 2 begint. */
  | 'LEGE_EERSTE_PAGINA'
  /**
   * FO-410 (batch-54) - alleen in de wizard-PREVIEW: het sjabloon is beheerst,
   * dus het definitieve document krijgt nog een exemplaar-volgnummer uit het
   * register. Een preview claimt dat nummer bewust niet (gat-vrije nummering).
   */
  | 'PREVIEW_ZONDER_EXEMPLAARNUMMER'
  /**
   * FO-453 (batch-61) - het sjabloon draagt tags waarvoor geen waarde bestond
   * (niet in de placeholder-configuratie en geen auto-fill). Ze zijn LEEG
   * gerenderd (nullGetter); het document is er, maar controleer de lege plekken.
   */
  | 'SJABLOON_TAGS_ZONDER_WAARDE'
  /**
   * FO-453 - de placeholder-configuratie loopt achter op de DOCX (nieuwe tags
   * in het sjabloon, of configuratie-velden die niet meer in het sjabloon
   * staan). Nooit blokkerend: een herscan brengt ze weer gelijk.
   */
  | 'CONFIG_WIJKT_AF'
  /**
   * FO-459 (batch-62) - de briefpapierkeuze: gekozen briefpapier ontbreekt (terugval
   * gebruikt), zonder handtekeningvak op een te ondertekenen document, of een
   * expliciete keuze over een sjabloon met eigen briefpapier (dubbele kop/voet).
   */
  | import('./briefpapier').BriefpapierWaarschuwingCode

/**
 * Eén waarschuwing bij een geslaagde generatie. Het document is altijd
 * geleverd; de melding hoort zichtbaar bij het resultaat (banner/toast), niet
 * alleen in de audit-metadata (FO-401 §3.2).
 */
export interface DocumentGeneratieWaarschuwing {
  code: DocumentGeneratieWaarschuwingCode
  melding: string
  /**
   * FO-453 - de betrokken placeholder-keys (bv. de leeg gerenderde tags), zodat
   * de UI ze als chips kan tonen naast de leesbare melding. Optioneel.
   */
  keys?: string[]
}

// ─── FO-453 (batch-61): tagfouten + scan-synchronisatie ──────────────────────

/**
 * Soort tagfout in een sjabloon, gespiegeld op de docxtemplater-foutcodes. Dit
 * zijn de ENIGE fouten die een generatie nog hard laten stoppen (render-
 * exceptie, FO-453 §3.3c): een tag die niet sluit, niet opent of dubbel opent.
 */
export type TemplateTagFoutId =
  | 'unclosed_tag'
  | 'unopened_tag'
  | 'duplicate_open_tag'
  | 'duplicate_close_tag'
  | 'onbekend'

/**
 * Eén tagfout, met precies wat/waar/wat-te-doen (FO-453 §3.5): de exacte tag,
 * het onderdeel van het document (hoofdtekst/koptekst/voettekst) en het
 * alineanummer daarin, plus het tekstfragment om de plek in Word terug te
 * vinden (Ctrl+F op `context`).
 */
export interface TemplateTagFout {
  id: TemplateTagFoutId
  /** De tag zoals gevonden (zonder delimiters), bv. `OPDRACHTGEVER_NAAM`. */
  tag: string
  /** Leesbare uitleg: "De tag {{X}} wordt niet gesloten". */
  uitleg: string
  /** Wat te doen in Word om het te herstellen. */
  advies: string
  /** OOXML-part (`word/document.xml`, `word/header1.xml`, ...). */
  part: string
  /** Leesbaar onderdeel: "hoofdtekst", "koptekst 1", "voettekst 2". */
  partLabel: string
  /** 1-based alineanummer binnen het onderdeel, of null als niet bepaalbaar. */
  alinea: number | null
  /** Tekstfragment rond de fout (max ~80 tekens). */
  context: string
}

/** Foutcode van de generate-routes bij een tagfout (422). */
export const TEMPLATE_TAG_FOUT_CODE = 'TEMPLATE_TAG_FOUT' as const

/**
 * `details` van de 422 `TEMPLATE_TAG_FOUT`-envelope: de wizard toont per fout
 * wat/waar/wat-te-doen en biedt de knop "Template herstellen" aan.
 */
export interface TemplateTagFoutDetails {
  tagFouten: TemplateTagFout[]
  /** Het sjabloon waar de fout in zit (voor de herstel-knop); null als onbekend. */
  templateId: string | null
  templateNaam: string | null
  /**
   * Een geregistreerd (onveranderlijk) sjabloon kan niet in-place hersteld
   * worden: dan moet beheer een nieuwe versie via het documentregister maken.
   */
  geregistreerd: boolean
}

/** Herkomst van een scan-synchronisatie (audit + UI). */
export type TemplateScanBron = 'UPLOAD' | 'HERSCAN' | 'HERSTEL'

/**
 * Uitkomst van de scan-synchronisatie (auto bij upload, handmatig via
 * "Herscan"/"Template herstellen"): wat er in de DOCX staat, wat de
 * configuratie daarop is aangepast, en welke tagfouten er (nog) zijn.
 */
export interface TemplateScanSyncResultaat {
  bron: TemplateScanBron
  gescandOp: string
  /** Alle canonieke gegevens (primaire schrijfwijze) in het sjabloon. */
  keysInSjabloon: string[]
  /** Configuratie-rijen die de sync als optioneel veld heeft toegevoegd. */
  nieuw: string[]
  /** Configuratie-rijen die niet (meer) in het sjabloon staan (gemarkeerd). */
  verdwenen: string[]
  /** Aantal rijen dat al klopte. */
  ongewijzigd: number
  /** Aantal over runs gesplitste tags dat de herstel-stap heeft samengevoegd. */
  hersteldeTags: number
  /** Overgebleven tagfouten (leeg = het sjabloon rendert). */
  tagFouten: TemplateTagFout[]
  /**
   * True als de configuratie NIET is weggeschreven omdat het sjabloon
   * geregistreerd (onveranderlijk) is; de verschillen zijn dan alleen gemeld.
   */
  alleenGemeld: boolean
}

/** Samenvatting van de laatste scan op het sjabloon (DTO, uit de scan-tabel). */
export interface TemplateScanStatusDTO {
  bron: TemplateScanBron
  gescandOp: string
  aantalKeys: number
  tagFouten: TemplateTagFout[]
  nieuweKeys: string[]
  verdwenenKeys: string[]
  hersteldeTags: number
}

/** Categorie van een document-template — spiegelt Prisma `DocumentTemplateCategory`. */
export type DocumentTemplateCategory =
  | 'HOOFDOVEREENKOMST'
  | 'OPDRACHTBEVESTIGING'
  | 'ARBEIDSOVEREENKOMST'
  | 'AUTOREGELING'
  | 'BRUIKLEEN'
  | 'STUDIEOVEREENKOMST'
  // FO-388 (batch-46) — aanzegbrief (wel/niet verlengen), scope MEDEWERKER.
  | 'AANZEGBRIEF'
  // FO-396 (batch-46) — uitdienst-documenten (MEDEWERKER-scope).
  | 'GETUIGSCHRIFT'
  | 'EINDE_DIENSTVERBAND_BRIEF'
  // FO-400 (batch-49) — projectovereenkomst-sjabloon (scope RELATIE).
  | 'PROJECTOVEREENKOMST'
  | 'OVERIG'
  // batch-67 hr-brieven (FO-506) — de zeven HR-brieven (scope MEDEWERKER). De
  // tag-scan BLOKKEERT bij deze categorieën op een ontbrekende verplichte tag
  // (`HR_BRIEF_VERPLICHTE_TAGS` in hr-besluiten.ts); losse ADD VALUE-migratie.
  | 'VERLENGING_BEPAALDE_TIJD'
  | 'OMZETTING_ONBEPAALDE_TIJD'
  | 'OPZEGGING_BEPAALDE_TIJD'
  | 'NON_ACTIEFSTELLING'
  | 'VSO'
  | 'ONTSLAG_STAANDE_VOET'
  | 'ACCEPTATIE_OPZEGGING'

export const DOCUMENT_TEMPLATE_CATEGORIES: readonly DocumentTemplateCategory[] = [
  'HOOFDOVEREENKOMST',
  'OPDRACHTBEVESTIGING',
  'ARBEIDSOVEREENKOMST',
  'AUTOREGELING',
  'BRUIKLEEN',
  'STUDIEOVEREENKOMST',
  'AANZEGBRIEF',
  'GETUIGSCHRIFT',
  'EINDE_DIENSTVERBAND_BRIEF',
  'PROJECTOVEREENKOMST',
  'OVERIG',
  // batch-67 hr-brieven (FO-506)
  'VERLENGING_BEPAALDE_TIJD',
  'OMZETTING_ONBEPAALDE_TIJD',
  'OPZEGGING_BEPAALDE_TIJD',
  'NON_ACTIEFSTELLING',
  'VSO',
  'ONTSLAG_STAANDE_VOET',
  'ACCEPTATIE_OPZEGGING',
] as const

export const DOCUMENT_TEMPLATE_CATEGORY_LABELS: Record<DocumentTemplateCategory, string> = {
  HOOFDOVEREENKOMST: 'Hoofdovereenkomst',
  OPDRACHTBEVESTIGING: 'Opdrachtbevestiging',
  ARBEIDSOVEREENKOMST: 'Arbeidsovereenkomst',
  AUTOREGELING: 'Autoregeling',
  BRUIKLEEN: 'Bruikleen',
  STUDIEOVEREENKOMST: 'Studieovereenkomst',
  AANZEGBRIEF: 'Aanzegbrief',
  GETUIGSCHRIFT: 'Getuigschrift',
  EINDE_DIENSTVERBAND_BRIEF: 'Einde-dienstverband-brief',
  PROJECTOVEREENKOMST: 'Projectovereenkomst',
  OVERIG: 'Overig',
  // batch-67 hr-brieven (FO-506)
  VERLENGING_BEPAALDE_TIJD: 'Brief verlenging bepaalde tijd',
  OMZETTING_ONBEPAALDE_TIJD: 'Brief omzetting naar onbepaalde tijd',
  OPZEGGING_BEPAALDE_TIJD: 'Brief opzegging bepaalde tijd (aanzegging)',
  NON_ACTIEFSTELLING: 'Brief non-actiefstelling',
  VSO: 'Vaststellingsovereenkomst (VSO)',
  ONTSLAG_STAANDE_VOET: 'Brief ontslag op staande voet',
  ACCEPTATIE_OPZEGGING: 'Brief bevestiging opzegging medewerker',
}

/**
 * Scope van een document-template — spiegelt Prisma `DocumentTemplateScope`.
 * RELATIE = klant-overeenkomsten (relatie-detail), MEDEWERKER = medewerker-
 * overeenkomsten, ONBOARDING = automatische onboarding-documentgeneratie
 * (gekoppeld via onboardingType). De generate-modal/-wizard tonen alleen
 * RELATIE/MEDEWERKER; ONBOARDING-templates worden auto-gerenderd.
 */
export type DocumentTemplateScope = 'RELATIE' | 'MEDEWERKER' | 'ONBOARDING'

export const DOCUMENT_TEMPLATE_SCOPES: readonly DocumentTemplateScope[] = [
  'RELATIE',
  'MEDEWERKER',
  'ONBOARDING',
] as const

export const DOCUMENT_TEMPLATE_SCOPE_LABELS: Record<DocumentTemplateScope, string> = {
  RELATIE: 'Relatie-overeenkomsten',
  MEDEWERKER: 'Medewerker-overeenkomsten',
  ONBOARDING: 'Onboarding-documenten',
}

/**
 * Eén keuze in de auto-fill-bron-dropdown van de placeholder-config-editor.
 * `value` is exact het `autoFillFrom`-formaat ("today", "employee.<veld>",
 * "entity.<veld>", "client.<veld>") dat de resolver in
 * `client-document-autofill.ts` begrijpt. `group` is de visuele kop in de
 * doorzoekbare dropdown (`Combobox`).
 */
export interface AutoFillSourceOption {
  value: string
  label: string
  group: string
}

/**
 * De employee-velden die als auto-fill-bron kiesbaar zijn. Spiegelt exact de
 * keys uit `buildEmployeeDocumentVariables` (single source of truth in
 * `apps/api/src/lib/employee-document-variables.ts`) plus de `fullAddress`-alias.
 * NB: leidinggevende/aantalDagen/study-velden blijven handmatig (ADDENDA
 * D.3/D.4/D.5) en staan hier dus bewust niet tussen.
 */
const EMPLOYEE_AUTOFILL_FIELDS: readonly string[] = [
  'voornaam',
  'achternaam',
  'naam',
  'voorletter',
  'geboortedatum',
  // FO-154 — geboorteplaats/nationaliteit/IBAN bestonden al op Employee maar
  // waren nog geen bron. IBAN wordt (net als BSN) gemaskeerd geresolved.
  'geboortePlaats',
  'nationaliteit',
  'iban',
  'adres',
  'straatnaam',
  'huisnummer',
  'postcode',
  'woonplaats',
  'bsn',
  'functie',
  'afdeling',
  'startDatum',
  'eindDatum',
  'contractType',
  'uren',
  'salaris',
  'uurloon',
  'caoSchaal',
  'caoTrede',
  'bvNaam',
  'bvAdres',
  'bvKvk',
  'datum',
  'jaartal',
]

/** Composiet-velden per adres-bron ("(postcode) plaats aan de straat huisnr" etc). */
const ADDRESS_COMPOSITES: readonly { field: string; suffix: string }[] = [
  { field: 'fullAddress', suffix: 'volledig adres' },
  { field: 'postcodePlaats', suffix: 'postcode + plaats' },
  { field: 'streetLine', suffix: 'straat + huisnummer' },
]

const ENTITY_AUTOFILL_FIELDS: readonly string[] = [
  'name',
  'kvkNumber',
  'street',
  'postalCode',
  'city',
]

/**
 * FO-154 — extra entiteit-bronnen met een eigen (leesbaar) label. De
 * ondertekenaar komt uit het WGV-ondertekenaar-register op de entiteit
 * (`wgvStandaardOndertekenaarUserId` -> User.name, `wgvContactpersoonFunctie`);
 * `land` is een constante "Nederland" (geen Entity-kolom, geen migratie).
 */
const ENTITY_EXTRA_AUTOFILL_OPTIONS: readonly AutoFillSourceOption[] = [
  {
    value: 'entity.ondertekenaarNaam',
    label: 'STREEP-entiteit - ondertekenaar (naam)',
    group: 'STREEP-entiteit',
  },
  {
    value: 'entity.ondertekenaarFunctie',
    label: 'STREEP-entiteit - ondertekenaar (functie)',
    group: 'STREEP-entiteit',
  },
  {
    value: 'entity.land',
    label: 'STREEP-entiteit - land (vast: Nederland)',
    group: 'STREEP-entiteit',
  },
]

/**
 * FO-154 — de voertuig-bronnen (groep "Voertuig"). Gevoed door de op peildatum
 * actieve VoertuigToewijzing van de medewerker (VAST wint van TIJDELIJK), dus
 * alleen zinvol in de MEDEWERKER-/ONBOARDING-scope. De veldnamen spiegelen de
 * keys die `buildVoertuigDocumentVariables` (apps/api) produceert.
 */
const VOERTUIG_AUTOFILL_OPTIONS: readonly AutoFillSourceOption[] = [
  { value: 'voertuig.kenteken', label: 'Voertuig - kenteken', group: 'Voertuig' },
  { value: 'voertuig.merk', label: 'Voertuig - merk', group: 'Voertuig' },
  { value: 'voertuig.model', label: 'Voertuig - model (uitvoering)', group: 'Voertuig' },
  { value: 'voertuig.merkModel', label: 'Voertuig - merk + model', group: 'Voertuig' },
  { value: 'voertuig.type', label: 'Voertuig - type (soort voertuig)', group: 'Voertuig' },
  { value: 'voertuig.tenaamstelling', label: 'Voertuig - tenaamstelling', group: 'Voertuig' },
  { value: 'voertuig.bijtelling', label: 'Voertuig - bijtelling (Ja/Nee)', group: 'Voertuig' },
  {
    value: 'voertuig.bijtellingMaandbedrag',
    label: 'Voertuig - bijtelling maandbedrag',
    group: 'Voertuig',
  },
  { value: 'voertuig.tankpas', label: 'Voertuig - tankpasnummer', group: 'Voertuig' },
  { value: 'voertuig.brandstof', label: 'Voertuig - brandstof', group: 'Voertuig' },
  { value: 'voertuig.bouwjaar', label: 'Voertuig - bouwjaar', group: 'Voertuig' },
]

/**
 * batch-55 bruikleen-docs (FO-421) — de item-bronnen (groep "Item"). Gevoed
 * door het in de bruikleen-wizard gekozen gebruiksitem: een voertuig of een
 * uniek middel (gereedschap, telefoon, simkaart, kar, koffer). De veldnamen
 * spiegelen de keys die `buildMiddelDocumentVariables` (apps/api) produceert;
 * velden die het gekozen itemtype niet kent blijven leeg (nooit verzonnen) en
 * verschijnen vóór het genereren in de lijst "ontbrekende gegevens".
 */
export const ITEM_AUTOFILL_FIELDS: readonly string[] = [
  'omschrijving',
  'soort',
  'identificatie',
  'kenteken',
  'serienummer',
  'assetTag',
  'imei',
  'telefoonnummer',
  'iccid',
  'provider',
  'merk',
  'model',
  'merkModel',
  'bouwjaar',
  'chassisnummer',
  'uitgiftedatum',
  'verwachteRetour',
  'aanschafwaarde',
]

const ITEM_AUTOFILL_OPTIONS: readonly AutoFillSourceOption[] = [
  { value: 'item.omschrijving', label: 'Item - omschrijving (artikel of merk + model)', group: 'Item' },
  { value: 'item.soort', label: 'Item - soort (voertuig, telefoon, gereedschap, kar, koffer)', group: 'Item' },
  { value: 'item.identificatie', label: 'Item - identificatie (kenteken / nummer / serienummer / IMEI)', group: 'Item' },
  { value: 'item.kenteken', label: 'Item - kenteken (voertuig)', group: 'Item' },
  { value: 'item.serienummer', label: 'Item - serienummer', group: 'Item' },
  { value: 'item.assetTag', label: 'Item - nummer / assettag', group: 'Item' },
  { value: 'item.imei', label: 'Item - IMEI (telefoon)', group: 'Item' },
  { value: 'item.telefoonnummer', label: 'Item - telefoonnummer', group: 'Item' },
  { value: 'item.iccid', label: 'Item - ICCID (simkaart)', group: 'Item' },
  { value: 'item.provider', label: 'Item - provider (simkaart)', group: 'Item' },
  { value: 'item.merk', label: 'Item - merk', group: 'Item' },
  { value: 'item.model', label: 'Item - model', group: 'Item' },
  { value: 'item.merkModel', label: 'Item - merk + model', group: 'Item' },
  { value: 'item.bouwjaar', label: 'Item - bouwjaar (voertuig)', group: 'Item' },
  { value: 'item.chassisnummer', label: 'Item - chassisnummer (voertuig)', group: 'Item' },
  { value: 'item.uitgiftedatum', label: 'Item - uitgiftedatum aan de medewerker', group: 'Item' },
  { value: 'item.verwachteRetour', label: 'Item - verwachte retourdatum', group: 'Item' },
  { value: 'item.aanschafwaarde', label: 'Item - aanschafwaarde', group: 'Item' },
]

const CLIENT_AUTOFILL_FIELDS: readonly string[] = [
  'name',
  'kvkNumber',
  'postalCode',
  'city',
  'street',
  'houseNumber',
]

/**
 * FO-401 (batch-49) - klant-bronnen die geen kale Client-kolom zijn: de
 * rechtsvorm-veilige naam (voorkomt "A. Jansen B.V. B.V."), het vestigingsadres
 * uit de PRIMAIRE LOCATIE van de relatie (zodat een sjabloon nooit een plaats
 * hoeft te misbruiken waar een adres hoort) en de tekenbevoegde contactpersoon.
 */
const CLIENT_EXTRA_AUTOFILL_OPTIONS: readonly AutoFillSourceOption[] = [
  {
    value: 'client.naamZonderRechtsvorm',
    label: 'Klant - naam zonder rechtsvorm (B.V./VOF eraf)',
    group: 'Klant',
  },
  {
    value: 'client.vestigingsAdres',
    label: 'Klant - vestigingsadres: straat + huisnummer (primaire locatie)',
    group: 'Klant',
  },
  {
    value: 'client.vestigingsPostcodePlaats',
    label: 'Klant - vestigingsadres: postcode + plaats (primaire locatie)',
    group: 'Klant',
  },
  {
    value: 'client.vestigingsadres',
    label: 'Klant - vestigingsadres volledig (primaire locatie)',
    group: 'Klant',
  },
  {
    value: 'client.contactpersoonNaam',
    label: 'Klant - contactpersoon (ondertekenaar)',
    group: 'Klant',
  },
  // FO-410 (batch-54) §B4 - de functie/rol van de tekenbevoegde, voor zinnen als
  // "rechtsgeldig vertegenwoordigd door {{naam}}, {{functie}}".
  {
    value: 'client.contactpersoonFunctie',
    label: 'Klant - contactpersoon: functie',
    group: 'Klant',
  },
]

/**
 * Alle kiesbare auto-fill-bronnen voor de placeholder-config-editor. De editor
 * filtert hieruit op scope (MEDEWERKER/ONBOARDING → today + employee.* +
 * voertuig.* + entity.*; RELATIE → today + client.* + entity.*) en rendert ze
 * als doorzoekbare, gegroepeerde dropdown. Vrije invoer blijft toegestaan (de
 * editor injecteert een onbekende bestaande waarde als losse "Aangepast"-optie).
 */
export const AUTOFILL_SOURCE_OPTIONS: readonly AutoFillSourceOption[] = [
  { value: 'today', label: 'Vandaag (datum van genereren)', group: 'Datum' },
  ...EMPLOYEE_AUTOFILL_FIELDS.map((field) => ({
    value: `employee.${field}`,
    label: `Medewerker - ${field}`,
    group: 'Medewerker',
  })),
  {
    value: 'employee.fullAddress',
    label: 'Medewerker - volledig adres',
    group: 'Medewerker',
  },
  ...VOERTUIG_AUTOFILL_OPTIONS,
  ...ITEM_AUTOFILL_OPTIONS,
  ...ENTITY_AUTOFILL_FIELDS.map((field) => ({
    value: `entity.${field}`,
    label: `STREEP-entiteit - ${field}`,
    group: 'STREEP-entiteit',
  })),
  ...ENTITY_EXTRA_AUTOFILL_OPTIONS,
  ...ADDRESS_COMPOSITES.map((c) => ({
    value: `entity.${c.field}`,
    label: `STREEP-entiteit - ${c.suffix}`,
    group: 'STREEP-entiteit',
  })),
  ...CLIENT_AUTOFILL_FIELDS.map((field) => ({
    value: `client.${field}`,
    label: `Klant - ${field}`,
    group: 'Klant',
  })),
  ...ADDRESS_COMPOSITES.map((c) => ({
    value: `client.${c.field}`,
    label: `Klant - ${c.suffix}`,
    group: 'Klant',
  })),
  ...CLIENT_EXTRA_AUTOFILL_OPTIONS,
] as const

/**
 * Declaratieve `autoFillFrom`-bron per (lowercase) placeholder-key. Eén
 * bron-van-waarheid voor de DOCX-scan-suggesties (api), de naam-suggestie in de
 * placeholder-config-editor (backoffice) en het sjabloon-audit-script (FO-154).
 * Verhuisd uit `apps/api/src/lib/document-template-scanner.ts` zodat alle drie
 * dezelfde map delen; de lookup (`guessAutoFill`) lowercaset de key.
 *
 * Bevat de echte `{{camelCase}}`-keys die `buildEmployeeDocumentVariables` /
 * `buildTemplateVariables` produceren (employee/entity/today), naast de
 * bestaande klant-keys (OPDRACHTGEVER, STREEP) zodat klant-template-scanning
 * blijft werken. Niet-afleidbare velden (datumOndertekening, bedragen, studie-/
 * bruikleen-velden, `leidinggevende` (geen manager-relatie, ADDENDA D.3) en
 * `personeelsnummer` (niet door `buildEmployeeDocumentVariables` geproduceerd))
 * staan hier NIET → `guessAutoFill` geeft undefined → handmatig veld. Cruciaal:
 * een key alleen hier opnemen als de bijbehorende bron hem óók echt vult,
 * anders classificeert hij als autofill maar rendert hij leeg. `jaartal`/`bsn`
 * hebben geen schone bronpad-string en blijven ook weg (ze worden via
 * DOCUMENT_AUTOFILL_KEYS alsnog als autofill geclassificeerd via key-naam-fill).
 */
export const AUTO_FILL_GUESSES: Readonly<Record<string, string>> = {
  // ── Medewerker (echte HR-template keys) ──────────────────────────────────
  voornaam: 'employee.voornaam',
  achternaam: 'employee.achternaam',
  naam: 'employee.naam',
  medewerker: 'employee.naam',
  voorletter: 'employee.voorletter',
  voorletters: 'employee.voorletter',
  geboortedatum: 'employee.geboortedatum',
  geboorteplaats: 'employee.geboortePlaats',
  nationaliteit: 'employee.nationaliteit',
  iban: 'employee.iban',
  rekeningnummer: 'employee.iban',
  adres: 'employee.adres',
  straatnaam: 'employee.straatnaam',
  huisnummer: 'employee.huisnummer',
  postcode: 'employee.postcode',
  woonplaats: 'employee.woonplaats',
  plaats: 'employee.woonplaats',
  functie: 'employee.functie',
  afdeling: 'employee.afdeling',
  startdatum: 'employee.startDatum',
  // Reële HR-template alias voor de startdatum (arbeidsovereenkomsten).
  datumindiensttreding: 'employee.startDatum',
  einddatum: 'employee.eindDatum',
  uurloon: 'employee.uurloon',
  salaris: 'employee.salaris',
  // `brutoMaandsalaris` = het maandsalaris; `salarisschaal` = de CAO-schaal
  // (NIET het bedrag) — die twee werden in de praktijk door elkaar gehaald.
  brutomaandsalaris: 'employee.salaris',
  salarisschaal: 'employee.caoSchaal',
  caoschaal: 'employee.caoSchaal',
  caotrede: 'employee.caoTrede',
  // Reële HR-template alias voor de werknemersnaam (volledige naam).
  werknemernaam: 'employee.naam',
  // Contractuele uren per week (Employee.contractHoursPerWeek).
  urenperweek: 'employee.uren',
  // ── Voertuig (FO-154 — actieve auto van de medewerker) ───────────────────
  // De losse woorden `merk`/`model`/`type` blijven bewust weg: te generiek om
  // blind aan het voertuig te koppelen (de admin kiest die zelf in de editor).
  kenteken: 'voertuig.kenteken',
  autokenteken: 'voertuig.kenteken',
  automerk: 'voertuig.merk',
  automodel: 'voertuig.model',
  autotype: 'voertuig.type',
  merkmodel: 'voertuig.merkModel',
  tenaamstelling: 'voertuig.tenaamstelling',
  autotenaamstelling: 'voertuig.tenaamstelling',
  voertuigtenaamstelling: 'voertuig.tenaamstelling',
  tankpasnummer: 'voertuig.tankpas',
  bijtellingmaandbedrag: 'voertuig.bijtellingMaandbedrag',
  brandstof: 'voertuig.brandstof',
  bouwjaar: 'voertuig.bouwjaar',
  // ── Entiteit (STREEP-BV op de overeenkomst) ──────────────────────────────
  bvnaam: 'entity.name',
  bvstreep: 'entity.name',
  bvadres: 'entity.fullAddress',
  bvkvk: 'entity.kvkNumber',
  kvk: 'entity.kvkNumber',
  'kvk-nummer': 'entity.kvkNumber',
  kvknummer: 'entity.kvkNumber',
  // Plaats van ondertekening = de vestigingsplaats van de STREEP-entiteit
  // (city), NIET de straat of het woonadres van de medewerker.
  plaatsondertekening: 'entity.city',
  ondertekenaar: 'entity.ondertekenaarNaam',
  ondertekenaarnaam: 'entity.ondertekenaarNaam',
  ondertekenaarfunctie: 'entity.ondertekenaarFunctie',
  land: 'entity.land',
  // Werkgever-varianten (o.a. toestemmingsverklaring auto buitenland).
  werkgevernaam: 'entity.name',
  werkgeveradres: 'entity.fullAddress',
  werkgeverpostcodeplaats: 'entity.postcodePlaats',
  werkgeverland: 'entity.land',
  // ── Item (FO-421 — het in de bruikleen-wizard gekozen gebruiksitem) ──────
  // Alleen keys die zonder item geen betekenis hebben; generieke woorden
  // (omschrijving/merk/model/soort) kiest de beheerder bewust in de editor.
  serienummer: 'item.serienummer',
  serial: 'item.serienummer',
  imei: 'item.imei',
  imeinummer: 'item.imei',
  assettag: 'item.assetTag',
  itemnummer: 'item.assetTag',
  identificatie: 'item.identificatie',
  iccid: 'item.iccid',
  simnummer: 'item.iccid',
  telefoonnummer: 'item.telefoonnummer',
  uitgiftedatum: 'item.uitgiftedatum',
  verwachteretour: 'item.verwachteRetour',
  retourdatum: 'item.verwachteRetour',
  aanschafwaarde: 'item.aanschafwaarde',
  // ── Datum (generieke datum + uitgiftedatum; ondertekendatum blijft handmatig) ─
  datum: 'today',
  datumuitgifte: 'today',
  // ── Klant — bestaande v3 keys (buildPlaceholderValues) ───────────────────
  opdrachtgever_naam: 'client.name',
  opdrachtgever_kvk: 'client.kvkNumber',
  opdrachtgever_postcode: 'client.postalCode',
  opdrachtgever_plaats: 'client.city',
  // FO-410 (batch-54) - de ontbrekende opdrachtgever-keys. `..._straat` is de
  // straatnaam ZONDER nummer; `..._adres` is straat + nummer (de samenstelling
  // komt uit de gedeelde mapper, niet uit de key-naam).
  opdrachtgever_straat: 'client.street',
  opdrachtgever_huisnummer: 'client.houseNumber',
  opdrachtgever_adres: 'client.vestigingsAdres',
  opdrachtgever_postcode_plaats: 'client.vestigingsPostcodePlaats',
  // FO-410 §B4/G3 - vertegenwoordiger/contactpersoon van de relatie stond leeg
  // terwijl de relatie hem kent.
  opdrachtgever_vertegenwoordiger: 'client.contactpersoonNaam',
  opdrachtgever_contactpersoon: 'client.contactpersoonNaam',
  contactpersoon_opdrachtgever: 'client.contactpersoonNaam',
  opdrachtgever_vertegenwoordiger_functie: 'client.contactpersoonFunctie',
  klant_kvk: 'client.kvkNumber',
  klant_straat: 'client.street',
  klant_huisnummer: 'client.houseNumber',
  klant_postcode: 'client.postalCode',
  klant_plaats: 'client.city',
  klant_contactpersoon: 'client.contactpersoonNaam',
  // ── Klant — camelCase keys uit de relatie-overeenkomsten ─────────────────
  // De opdrachtgever IS de klant (client.*), niet de STREEP-entiteit — dit
  // werd in de praktijk verward (opdrachtgeverStraat wees naar entity.*).
  opdrachtgevernaam: 'client.name',
  opdrachtgeverkvk: 'client.kvkNumber',
  opdrachtgeverpostcode: 'client.postalCode',
  opdrachtgeverplaats: 'client.city',
  opdrachtgeverstraat: 'client.street',
  opdrachtgeverhuisnummer: 'client.houseNumber',
  // ── Klant — FO-401 (batch-49): rechtsvorm-veilige naam, vestigingsadres uit
  // de primaire locatie en het ondertekenblok. `datum_ondertekening` staat hier
  // bewust NIET: die blijft leeg tot er getekend wordt (FO-401 G7).
  klantnaam: 'client.name',
  klantnaam_zonder_rechtsvorm: 'client.naamZonderRechtsvorm',
  klantnaamzonderrechtsvorm: 'client.naamZonderRechtsvorm',
  klant_adres: 'client.vestigingsAdres',
  klantadres: 'client.vestigingsAdres',
  klant_postcode_plaats: 'client.vestigingsPostcodePlaats',
  klantpostcodeplaats: 'client.vestigingsPostcodePlaats',
  klant_vestigingsadres: 'client.vestigingsadres',
  klantvestigingsadres: 'client.vestigingsadres',
  ondertekenaar_klant: 'client.contactpersoonNaam',
  ondertekenaarklant: 'client.contactpersoonNaam',
  ondertekenaar_streep: 'entity.ondertekenaarNaam',
  ondertekenaarstreep: 'entity.ondertekenaarNaam',
  // ── STREEP-entiteit — bestaande klant-template keys ──────────────────────
  streep_entiteit: 'entity.name',
  streep_kvk: 'entity.kvkNumber',
  // FO-410 (batch-54): `..._adres` = straat + huisnummer en
  // `..._postcode_plaats` = postcode + plaats. Ze wezen naar de KALE kolommen
  // (`entity.street` / `entity.postalCode`), waardoor de declaratieve bron een
  // half adres opleverde dat de correcte hardgecodeerde waarde overschreef -
  // dezelfde klasse fout als G2 aan de klant-kant.
  streep_adres: 'entity.streetLine',
  streep_straat: 'entity.street',
  streep_huisnummer: 'entity.houseNumber',
  streep_postcode: 'entity.postalCode',
  streep_plaats: 'entity.city',
  streep_postcode_plaats: 'entity.postcodePlaats',
  // camelCase varianten (relatie-overeenkomsten).
  streepentiteit: 'entity.name',
  streepbv: 'entity.name',
  streepkvk: 'entity.kvkNumber',
  kvkbv: 'entity.kvkNumber',
}

/**
 * De guess-map op CANONIEKE key (FO-410, batch-54). `OPDRACHTGEVER_STRAAT`,
 * `opdrachtgever_straat`, `Opdrachtgever straat` en `opdrachtgeverstraat` moeten
 * allemaal dezelfde bron voorstellen - anders krijgt de ene schrijfwijze in het
 * sjabloon wél een autofill-bron en de andere niet (precies gebrek G1/G2).
 * Bij twee raw-keys die naar dezelfde canonieke vorm normaliseren wint de eerste;
 * de map is daarom betekenis-consistent gehouden.
 */
const AUTO_FILL_GUESSES_CANONIEK: Readonly<Record<string, string>> = (() => {
  const out: Record<string, string> = {}
  for (const [rawKey, bron] of Object.entries(AUTO_FILL_GUESSES)) {
    const canoniek = canonicalPlaceholderKey(rawKey)
    if (canoniek && !(canoniek in out)) out[canoniek] = bron
  }
  return out
})()

/**
 * Naam-gebaseerde bron-suggestie voor een placeholder-key. Case-, spatie- en
 * scheidingsteken-ongevoelig (canonieke key, FO-410): de scanner, de
 * config-editor en het audit-script stellen zo voor elke schrijfwijze van
 * hetzelfde gegeven dezelfde bron voor.
 */
/**
 * FO-421 (batch-55) - sjablooncategorieën die als bruikleen-/itemdocument
 * gelden: bruikleen zelf én de autoregeling (auto met/zonder privégebruik is
 * óók een medewerker+voertuig-document). Eén bron voor de bruikleen-wizard
 * (API) én de scope-/categorie-bewuste naam-suggesties hieronder.
 */
export const BRUIKLEEN_TEMPLATE_CATEGORIES: readonly DocumentTemplateCategory[] = [
  'BRUIKLEEN',
  'AUTOREGELING',
]

/** Context voor `guessAutoFill`: welk sjabloon wordt gescand/geconfigureerd? */
export interface AutoFillGuessContext {
  scope?: DocumentTemplateScope
  category?: DocumentTemplateCategory
}

/**
 * Mag een `item.*`-bron voor dit sjabloon worden VOORGESTELD? Alleen de
 * bruikleen-wizard geeft een gekozen item mee, en die biedt uitsluitend
 * medewerker-sjablonen met categorie Bruikleen/Autoregeling aan. Elders zou
 * een `{{serienummer}}` als autofill classificeren maar leeg renderen - precies
 * wat het commentaar boven `AUTO_FILL_GUESSES` verbiedt. Expliciet kiezen in de
 * config-editor blijft voor elk medewerker-sjabloon mogelijk.
 */
export function isItemBronVoorstelToegestaan(ctx: AutoFillGuessContext | undefined): boolean {
  return (
    ctx?.scope === 'MEDEWERKER' &&
    ctx.category !== undefined &&
    BRUIKLEEN_TEMPLATE_CATEGORIES.includes(ctx.category)
  )
}

export function guessAutoFill(key: string, ctx?: AutoFillGuessContext): string | undefined {
  const canoniek = canonicalPlaceholderKey(key)
  // FO-421 — in een bruikleen-/autoregeling-sjabloon is "datum uitgifte" de
  // uitgiftedatum van het gekozen item, niet de generatiedatum: een medewerker
  // tekent anders een overeenkomst met de verkeerde datum. Buiten die context
  // blijft de generieke `today`-gok.
  if (canoniek === 'datumuitgifte' && isItemBronVoorstelToegestaan(ctx)) return 'item.uitgiftedatum'
  const bron = AUTO_FILL_GUESSES_CANONIEK[canoniek]
  if (bron?.startsWith('item.') && !isItemBronVoorstelToegestaan(ctx)) return undefined
  return bron
}

/**
 * Mag een `autoFillFrom`-bron gebruikt worden binnen deze template-scope?
 * Eén bron van waarheid voor de scope-filter in de placeholder-config-editor
 * en het sjabloon-audit-script (FO-154): employee-/voertuig-bronnen volgen de
 * medewerker (MEDEWERKER/ONBOARDING), klant-bronnen de relatie (RELATIE);
 * entiteit en `today` zijn overal geldig.
 */
export function isAutoFillSourceAllowedForScope(
  scope: DocumentTemplateScope,
  source: string,
): boolean {
  if (source.startsWith('employee.') || source.startsWith('voertuig.')) {
    return scope === 'MEDEWERKER' || scope === 'ONBOARDING'
  }
  // FO-421 — het item komt uit de bruikleen-wizard (medewerker-sjablonen); de
  // onboarding-generator kent geen gekozen item.
  if (source.startsWith('item.')) return scope === 'MEDEWERKER'
  if (source.startsWith('client.')) return scope === 'RELATIE'
  return true
}

/** Body voor PUT /client-document-templates/:id/placeholder-config. */
export interface UpdatePlaceholderConfigBody {
  placeholderConfig: PlaceholderConfig[]
}

/**
 * Body voor POST /generated-documents — klant-loze generate-flow. `clientId`
 * mag null zijn (handmatig ingevulde overeenkomst zonder gekoppelde relatie).
 */
export interface GenerateDocumentBody {
  templateId: string
  placeholderValues: Record<string, string>
  clientId?: string | null
  entityId?: string | null
}
